// Lazy-init WASM license verifier.
//
// On first call, fetches /ofengine_license_wasm_bg.wasm + instantiates.
// Subsequent calls reuse the loaded module. The .wasm binary is the only
// trustworthy source of tier decisions in the frontend — JS callers
// (including useTier) MUST go through these helpers rather than reading
// `license.tier` from AppContext directly.

import init, {
  tokenTier,
  tierAtLeast,
  isReadOnly,
  tokenInfo,
} from './ofengine_license_wasm';

let readyPromise = null;
let loadFailed = false;

export function ensureWasmReady() {
  if (readyPromise) return readyPromise;
  // Let wasm-bindgen resolve the bundled .wasm — webpack 5 in CRA v5
  // statically resolves `new URL('./foo.wasm', import.meta.url)` and emits
  // a hashed asset alongside the JS chunk.
  readyPromise = init()
    .then(() => true)
    .catch((err) => {
      // WASM load failed (no browser support, fetch error, etc.). Cache the
      // failure so we don't retry on every call. Callers that need a verdict
      // fall back to the JS-side AppContext license.
      // eslint-disable-next-line no-console
      console.warn('ofengine license-wasm load failed:', err);
      loadFailed = true;
      return false;
    });
  return readyPromise;
}

export function isWasmLoaded() {
  return readyPromise && !loadFailed;
}

export function wasmFailed() {
  return loadFailed;
}

/**
 * Verify a token and return the tier it asserts.
 * Returns '' (empty string) on any failure: token missing, signature
 * invalid, domain mismatch, expired, or WASM unavailable.
 */
export async function verifyTokenTier(token, currentDomain) {
  if (!token || !currentDomain) return '';
  const ok = await ensureWasmReady();
  if (!ok) return '';
  try {
    return tokenTier(token, currentDomain);
  } catch {
    return '';
  }
}

export async function verifyTierAtLeast(token, currentDomain, neededTier) {
  if (!token || !currentDomain) return false;
  const ok = await ensureWasmReady();
  if (!ok) return false;
  try {
    return tierAtLeast(token, currentDomain, neededTier);
  } catch {
    return false;
  }
}

export async function verifyIsReadOnly(token, currentDomain) {
  if (!token || !currentDomain) return false;
  const ok = await ensureWasmReady();
  if (!ok) return false;
  try {
    return isReadOnly(token, currentDomain);
  } catch {
    return false;
  }
}

export async function verifyTokenInfo(token, currentDomain) {
  if (!token || !currentDomain) return null;
  const ok = await ensureWasmReady();
  if (!ok) return null;
  try {
    return tokenInfo(token, currentDomain);
  } catch {
    return null;
  }
}
